/**
 * /admin/users-ops — "Users" operations hub. The people-side twin of
 * the Operations page: daily member-health cards, tap to jump into the
 * relevant tool. Fed by GET /api/admin/users-ops/summary.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { ArrowsClockwiseIcon as ArrowsClockwise, WarningIcon as Warning, InfoIcon as Info, CheckCircleIcon as CheckCircle, ArrowRightIcon as ArrowRight, UsersThreeIcon as UsersThree } from "@phosphor-icons/react";

const SEVERITY_STYLES = {
  critical: { border: "border-[#C1272D]/40", bg: "bg-[#C1272D]/5", text: "text-[#C1272D]", icon: Warning },
  warn:     { border: "border-[#8A6A00]/40", bg: "bg-[#FFF8E6]",   text: "text-[#8A6A00]", icon: Warning },
  info:     { border: "border-[#E5E5E5]",    bg: "bg-white",       text: "text-[#0E0E0E]", icon: Info },
  ok:       { border: "border-[#E5E5E5]",    bg: "bg-white",       text: "text-emerald-700", icon: CheckCircle },
};

export default function AdminUsersOps() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [nudging, setNudging] = useState(false);
  const [winBacking, setWinBacking] = useState(null);
  const [approvalsTotal, setApprovalsTotal] = useState(0);
  const [leaders, setLeaders] = useState([]);

  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/admin/users-ops/referral-leaderboard")
      .then((r) => setLeaders(r.data?.leaders || []))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/admin/approvals")
      .then((r) => setApprovalsTotal(r.data?.totals?.total || 0))
      .catch(() => {});
  }, [user]);

  const sendWinBack = async (e, segment, count) => {
    e.stopPropagation();
    if (!window.confirm(`Send the "look what's new" win-back email to this list (~${count} members)? Anyone emailed in the last 30 days is skipped automatically.`)) return;
    setWinBacking(segment);
    try {
      const r = await api.post(`/admin/users-ops/win-back/${segment}`);
      const { sent } = r.data;
      toast.success(sent > 0 ? `Win-back email sent to ${sent} member${sent === 1 ? "" : "s"}.` : "No one to email — everyone was reached in the last 30 days.");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Send failed."); }
    finally { setWinBacking(null); }
  };

  const sendPersonaNudge = async (e) => {
    e.stopPropagation();
    if (!window.confirm("Nudge everyone without a persona (inbox + push + email)? Members nudged in the last 14 days are skipped automatically.")) return;
    setNudging(true);
    try {
      const r = await api.post("/admin/users-ops/nudge-personas");
      const { nudged, eligible } = r.data;
      toast.success(nudged > 0 ? `Nudged ${nudged} member${nudged === 1 ? "" : "s"}.` : "No one to nudge — everyone was reached in the last 14 days.");
      if (eligible > nudged) toast.info(`${eligible - nudged} send(s) failed — check logs.`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Nudge failed."); }
    finally { setNudging(false); }
  };

  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/users-ops/summary");
      setData(r.data);
    } catch { /* cards just don't render */ }
    finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => {
    if (!isAdmin) return undefined;
    load();
    const iv = setInterval(load, 300000);
    return () => clearInterval(iv);
  }, [isAdmin, load]);

  if (user && !isAdmin) return <Navigate to="/dashboard" replace />;

  const cards = data?.cards || [];
  const attention = cards.filter((c) => c.severity === "warn" || c.severity === "critical");

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto" data-testid="admin-users-ops-page">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#6B6B6B] mb-2">Admin</p>
            <h1 className="font-spectral font-light text-4xl sm:text-5xl tracking-tight flex items-center gap-3">
              Users
            </h1>
            <p className="text-sm text-[#6B6B6B] mt-2 max-w-xl">
              Everything about your members in one glance — growth, activation,
              quiet accounts, moderation, and chat safety. Auto-refreshes every 5 minutes.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => { setRefreshing(true); load(); }}
              disabled={refreshing}
              className="rounded-full"
              data-testid="users-ops-refresh"
            >
              <ArrowsClockwise size={14} className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Link to="/admin/users" className="text-sm text-[#6B6B6B] hover:text-ink underline underline-offset-4" data-testid="users-ops-full-list-link">
              Full user list →
            </Link>
            <Link to="/admin/personas" className="text-sm text-[#6B6B6B] hover:text-ink underline underline-offset-4" data-testid="users-ops-personas-link">
              Personas →
            </Link>
          </div>
        </div>

        {approvalsTotal > 0 && (
          <Link
            to="/admin/approvals"
            className="mb-6 flex items-center justify-between gap-3 rounded-xl border border-[#C9A227]/50 bg-[#FBF7EC] px-4 py-3 hover:border-[#C9A227] transition-colors"
            data-testid="users-hub-approvals-banner"
          >
            <span className="text-sm font-semibold text-[#8A6A00]">
              {approvalsTotal} approval{approvalsTotal === 1 ? "" : "s"} waiting on you — transfers, kennel claims, reports & applications
            </span>
            <span className="text-sm font-bold text-[#8A6A00]">Review →</span>
          </Link>
        )}

        {attention.length > 0 && (
          <div className="rounded-xl border border-[#C9A227]/50 bg-[#FFF8E6] p-4 mb-6 flex items-start gap-3" data-testid="users-ops-attention">
            <Warning size={18} weight="fill" className="text-[#8A6A00] mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-spectral text-lg text-ink">{attention.length} thing{attention.length === 1 ? "" : "s"} to review</p>
              <p className="text-[12px] text-[#6B6B6B]">
                Tap any card to jump into the relevant page. Last refreshed {data?.generated_at ? new Date(data.generated_at).toLocaleString() : "…"}.
              </p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(9)].map((_, i) => <Skeleton key={i} className="h-40 rounded-xl" />)}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {cards.map((c) => {
              const style = SEVERITY_STYLES[c.severity] || SEVERITY_STYLES.info;
              const Icon = c.severity === "ok" || (c.count === 0 && c.severity === "info") ? CheckCircle : style.icon;
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => c.link && navigate(c.link)}
                  className={`text-left rounded-xl border ${style.border} ${style.bg} p-5 transition-all hover:shadow-[0_6px_20px_rgba(0,0,0,0.06)] hover:-translate-y-0.5 group`}
                  data-testid={`users-ops-card-${c.key}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] ${c.count > 0 && (c.severity === "warn" || c.severity === "critical") ? style.text : "text-[#3a7d44]"}`}>
                      <Icon size={13} weight="fill" /> {c.label}
                    </span>
                    <ArrowRight size={13} className="text-[#B0B0B0] group-hover:text-ink transition-colors flex-shrink-0" />
                  </div>
                  <div className={`font-spectral text-4xl leading-none mb-2 ${c.count > 0 && (c.severity === "warn" || c.severity === "critical") ? style.text : "text-[#1A6E36]"}`}>
                    {c.count}
                  </div>
                  <p className="text-[12px] text-[#6B6B6B] leading-snug">{c.hint}</p>
                  {c.key === "no_persona" && c.count > 0 && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={sendPersonaNudge}
                      onKeyDown={(e) => e.key === "Enter" && sendPersonaNudge(e)}
                      className={`inline-flex items-center gap-1 mt-3 rounded-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] bg-ink text-white hover:bg-crimson transition-colors ${nudging ? "opacity-50 pointer-events-none" : ""}`}
                      data-testid="users-ops-nudge-personas"
                    >
                      {nudging ? "Sending…" : "Send nudge to all →"}
                    </span>
                  )}
                  {c.win_back && c.count > 0 && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => sendWinBack(e, c.key === "never_returned" ? "never_returned" : "gone_quiet", c.count)}
                      onKeyDown={(e) => e.key === "Enter" && sendWinBack(e, c.key === "never_returned" ? "never_returned" : "gone_quiet", c.count)}
                      className={`inline-flex items-center gap-1 mt-3 rounded-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] bg-ink text-white hover:bg-crimson transition-colors ${winBacking ? "opacity-50 pointer-events-none" : ""}`}
                      data-testid={`users-ops-winback-${c.key}`}
                    >
                      {winBacking ? "Sending…" : "Email this list →"}
                    </span>
                  )}
                  {(c.win_back || c.key === "no_persona") && c.last_nudge && (
                    <span className="block mt-1.5 text-[10px] text-[#9A9A9A]" data-testid={`users-ops-last-send-${c.key}`}>
                      Last sent {new Date(c.last_nudge.at).toLocaleDateString()} · {c.last_nudge.nudged} member{c.last_nudge.nudged === 1 ? "" : "s"} · by {c.last_nudge.by}. {c.win_back ? "30" : "14"}-day per-member cooldown applies automatically.
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Referral leaderboard — best advocates */}
        <div className="mt-10 bg-white border border-[#EAEAEA] rounded-xl overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.04)]" data-testid="referral-leaderboard">
          <div className="p-5 border-b border-[#F0F0F0]">
            <h2 className="font-spectral text-2xl">Referral leaderboard</h2>
            <p className="text-sm text-[#6B6B6B] mt-1">Your best advocates — members whose invite links brought in new signups. Worth a thank-you (or a reward).</p>
          </div>
          {leaders.length === 0 ? (
            <div className="p-8 text-center" data-testid="leaderboard-empty">
              <p className="text-[14px] text-[#6B6B6B]">No referral signups yet — the board fills in as invite links get used.</p>
              <div className="flex items-center justify-center gap-3 mt-4 flex-wrap">
                <Link to="/admin/share-invite" className="inline-flex items-center gap-1.5 bg-ink hover:bg-crimson text-white rounded-full px-4 py-2 text-[12.5px] font-semibold transition-colors" data-testid="leaderboard-share-cta">
                  Send your invite scripts →
                </Link>
                <Link to="/invite" className="text-[12.5px] text-crimson underline underline-offset-2" data-testid="leaderboard-member-cta">
                  Members' invite page
                </Link>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-[#F5F5F5]">
              {leaders.map((l, i) => (
                <div key={l.user_id} className="px-5 py-3 flex items-center gap-4" data-testid={`leaderboard-row-${i}`}>
                  <span className={`inline-flex h-8 w-8 items-center justify-center rounded-full font-bold text-[13px] shrink-0 ${
                    i === 0 ? "bg-gold-gradient text-ink" : "bg-[#F4F2ED] text-[#8A8477]"
                  }`}>{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-[14px] truncate">{l.name}</p>
                    {l.email && <p className="text-[11.5px] text-[#9A9A9A] truncate">{l.email}</p>}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-spectral text-xl leading-none">{l.invites}</p>
                    <p className="text-[10.5px] text-[#9A9A9A] uppercase tracking-wider">invite{l.invites === 1 ? "" : "s"}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="text-[11px] text-[#9A9A9A] mt-8 flex items-center gap-1.5">
          <UsersThree size={13} weight="duotone" /> Tip: "Signed up, never returned" and "Gone quiet" are your win-back email lists — the Users page filters match these cards exactly.
        </p>
      </div>
    </AppShell>
  );
}
